import { useMemo, useState } from 'react';

import {
  Box,
  Container,
  DownloadButton,
  downloadCsv,
  Flex,
  HeadCellSortState,
  Headings,
  LoadingPage,
  Pagination,
  Text,
  toggleSortStates,
  usePagination,
} from '@foradevelopment/fora-ui';
import { ThunkRequestStatus } from '@fora-monorepo/webapp-types';
import { ReportedPayrollsRequestInput } from '@fora-monorepo/employer-my-pages-common/types';

import { PayHistoryTable } from '../components/PayHistoryTable';
import {
  defaultPayHistoryListSortState,
  PayHistorySortIdKeys,
  PayHistorySortState,
} from '../components/PayHistoryTable/PayHistoryListSortState';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { sortPayHistoryListIndices } from '../../common/utils/payHistorySorting';
import { createPayHistoryCSV } from '../../redux/payHistory/payHistoryThunks';
import { IntroText } from '../../components/IntroText/IntroText';
import { HistoryFilters } from '../../components';
import { useTypedTranslation } from '../../i18n/i18n';

const itemsPerPage = 25;

export const PayHistoryView = () => {
  const { t } = useTypedTranslation();
  const dispatch = useAppDispatch();
  const administrableEmployers = useAppSelector(state => state.employer.administrableEmployers);
  const payHistoryList = useAppSelector(state => state.payHistory.payHistory);
  const isLoadingPayHistory = useAppSelector(state => state.payHistory.loadingPayHistory);

  const [createCSVFilters, setCreateCSVFilters] = useState<ReportedPayrollsRequestInput>({
    payMonths: [],
    partnerNumberIndividuals: [],
    partnerNumberOrganization: '',
  });

  const [tableSortState, setTableSortState] = useState<PayHistorySortState>({
    ...defaultPayHistoryListSortState,
    period: HeadCellSortState.Desc,
  });

  const createCSV = async () => {
    const PartnerNumberOrganizationString =
      administrableEmployers.find(e => e.partnerNumber === createCSVFilters.partnerNumberOrganization)
        ?.organizationName ?? createCSVFilters.partnerNumberOrganization;

    const result = await dispatch(createPayHistoryCSV(createCSVFilters));
    if (createPayHistoryCSV.fulfilled.match(result)) {
      if (result.payload?.base64EncodedFile) {
        downloadCsv(result.payload.base64EncodedFile, `Fora_Registrerade_löner-${PartnerNumberOrganizationString}.csv`);
        return null;
      }
      return t('errorMessages.somethingWentWrongNoFileTryLater');
    } else {
      return t('errorMessages.somethingWentWrongWithCsvTryAgain');
    }
  };

  const sortedPayHistoryListIndices = useMemo(() => {
    const reportHistoryListIndices = payHistoryList.reportedPayrolls.map((_, ind) => ind);
    return sortPayHistoryListIndices({
      payHistoryList: payHistoryList.reportedPayrolls,
      sortState: tableSortState,
      indices: reportHistoryListIndices,
    });
  }, [payHistoryList, tableSortState]);

  const sortTable = (sortId: PayHistorySortIdKeys) => {
    setTableSortState(state => {
      // Reset from initial state to make sure that only one column is sorted at a time
      const newState = { ...defaultPayHistoryListSortState };
      newState[sortId] = toggleSortStates(state[sortId]);
      return newState;
    });
  };

  const { paginationProps, paginatedListIndices } = usePagination(sortedPayHistoryListIndices, itemsPerPage);

  return (
    <>
      <IntroText title={t('section.payrollHistroy.title')}>
        <Container>
          <Text size="medium">{t('section.payrollHistroy.description')}</Text>
        </Container>
      </IntroText>
      <HistoryFilters changeFilter={setCreateCSVFilters} variant="pay" />
      <Flex justifyContent="space-between" marginTop={40} marginBottom={20}>
        <Box>
          <Text> </Text>
          {/* TODO: */}
          {/* <Text>Visar {payHistoryList.reportedPayrolls.length} av TODO poster</Text> */}
        </Box>

        {(!!payHistoryList.reportedPayrolls.length || payHistoryList.tooManyRecords) && (
          <Box>{<DownloadButton name={t('common.filter.exportAsCsv')} download={createCSV} />}</Box>
        )}
      </Flex>
      <Container>
        {isLoadingPayHistory === ThunkRequestStatus.Pending ? (
          <LoadingPage />
        ) : payHistoryList.tooManyRecords ? (
          <CanNotShowPayHistory />
        ) : !payHistoryList.reportedPayrolls.length &&
          // selectedPartnerNumberOrganization &&
          isLoadingPayHistory === ThunkRequestStatus.Fulfilled ? (
          <NoPayHistory />
        ) : isLoadingPayHistory === null ? (
          <NoSelectedOrganizationInfo />
        ) : isLoadingPayHistory === ThunkRequestStatus.Rejected ? (
          <ErrorWhenDataWasLoaded />
        ) : (
          <>
            <Container style={{ overflowX: 'auto' }}>
              <PayHistoryTable
                payHistory={payHistoryList.reportedPayrolls}
                payHistoryListIndices={paginatedListIndices}
                sortState={tableSortState}
                sortTable={sortTable}
              />
            </Container>
            {!!payHistoryList.reportedPayrolls.length && <Pagination {...paginationProps} />}
          </>
        )}
      </Container>
    </>
  );
};

const NoPayHistory = () => {
  const { t } = useTypedTranslation();

  return (
    <Box backgroundColor="babyBlue" paddingBottom={40} paddingLeft={40} paddingRight={40} paddingTop={40}>
      <Flex justifyContent="center" rowGap={20} flexDirection="column">
        <Headings.H3>{t('infoMessages.filter.noPayrollsTitle')}</Headings.H3>
        <Text>{t('infoMessages.filter.noPayrollsText')}</Text>
      </Flex>
    </Box>
  );
};

const CanNotShowPayHistory = () => {
  const { t } = useTypedTranslation();

  return (
    <Box backgroundColor="babyBlue" paddingBottom={40} paddingLeft={40} paddingRight={40} paddingTop={40}>
      <Flex justifyContent="center" rowGap={20} flexDirection="column">
        <Headings.H3>{t('infoMessages.filter.tooManyResultsTitle')}</Headings.H3>
        <Text>{t('infoMessages.filter.tooManyResultsText')}</Text>
      </Flex>
    </Box>
  );
};

const NoSelectedOrganizationInfo = () => {
  const { t } = useTypedTranslation();

  return (
    <Box backgroundColor="babyBlue" paddingBottom={40} paddingLeft={40} paddingRight={40} paddingTop={40}>
      <Flex justifyContent="center" rowGap={20} flexDirection="column">
        <Headings.H3>{t('infoMessages.filter.noCompanyTitle')} </Headings.H3>
        <Text>{t('infoMessages.filter.noCompanyText')} </Text>
      </Flex>
    </Box>
  );
};

const ErrorWhenDataWasLoaded = () => {
  const { t } = useTypedTranslation();
  return (
    <Box backgroundColor="babyPink" paddingBottom={40} paddingLeft={40} paddingRight={40} paddingTop={40}>
      <Flex justifyContent="center" rowGap={20} flexDirection="column">
        <Headings.H3>{t('errorMessages.filter.couldNotFetchData')}</Headings.H3>
        <Text>{t('errorMessages.filter.couldNotFetchDataText')} </Text>
      </Flex>
    </Box>
  );
};
